import { createApi } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { type BillingTransaction, type BillingTransactionsResponse } from '../BillingTransactionsTab.types';

const COLLECTION = 'billing_transactions';

function recordFromResponse(response: BillingTransactionsResponse): BillingTransaction {
    const record = response?.contents?.billingTransactions?.[0];
    if (!record) throw new Error('Unexpected response shape from the billing transaction save');
    return record;
}

export const billingTransactionsApi = createApi('billingTransactionsApi', {
    ...getBackRoyalApiBaseConfig('billingTransactionsApi'),
    tagTypes: ['BillingTransactions'],
    endpoints: builder => ({
        getBillingTransactions: builder.query<BillingTransaction[], string>({
            query: ownerId => ({
                url: `${COLLECTION}.json`,
                params: { ownerId },
            }),

            // The index has no ORDER BY (BillingTransaction.all_for_owner_id), so the default sort
            // lives here rather than in the table.
            transformResponse: (response: BillingTransactionsResponse) =>
                [...(response.contents.billingTransactions || [])].sort(
                    (a, b) => (b.transactionTime || 0) - (a.transactionTime || 0),
                ),

            providesTags: ['BillingTransactions'],
        }),

        createBillingTransaction: builder.mutation<BillingTransaction, { record: BillingTransaction; ownerId: string }>(
            {
                query: ({ record, ownerId }) => ({
                    method: 'POST',
                    url: `${COLLECTION}.json`,

                    // meta.owner_id is required by Api::BillingTransactionsController#create.
                    body: { record, meta: { ownerId } },
                }),
                transformResponse: recordFromResponse,
                invalidatesTags: ['BillingTransactions'],

                /*
                    The shared base query retries once on a disconnect or a 502/504, and RTK applies
                    that to mutations too. Rails may already have committed the transaction, so a
                    retry would create a second one. The Angular $http path never retried.
                */
                extraOptions: { maxRetries: 0 },
            },
        ),

        updateBillingTransaction: builder.mutation<BillingTransaction, { record: BillingTransaction }>({
            query: ({ record }) => ({
                method: 'PUT',
                url: `${COLLECTION}/${record.id}.json`,
                body: { record },
            }),
            transformResponse: recordFromResponse,
            invalidatesTags: ['BillingTransactions'],
        }),

        deleteBillingTransaction: builder.mutation<void, string>({
            query: id => ({
                method: 'DELETE',
                url: `${COLLECTION}/${id}.json`,
            }),
            invalidatesTags: ['BillingTransactions'],
        }),
    }),
});

export default billingTransactionsApi;
